import { useCallback, useState } from 'react';

import { resendPasswordOtp, sendPasswordOtp, verifyPasswordOtp } from '../api';
import { PROFILE_COPY } from '../constants';
import { useIsDealer } from '../hooks';
import type { ProfileOtpDispatch } from '../schemas';
import { useProfileModalStore } from '../use-profile-modal-store';

import { NewPasswordStep } from './new-password-step';
import { SendCodeStep } from './send-code-step';
import { VerificationCodeStep } from './verification-code-step';

type Step =
  | { name: 'send' }
  | { name: 'code'; dispatch: ProfileOtpDispatch }
  | { name: 'new-password' };

const COPY = PROFILE_COPY.changePassword;

/** 117855 §1–§12. Cancel, ✕ or Escape closes the whole flow (§12a); expiry — the client timer or
 *  a server "session over" code — goes back to Send Code (§4a/§4b, grilling Q2). Unmounting is what
 *  makes a reopen start at Send Code (§12b); the server discards the earlier verification on every
 *  send-otp, so nothing is reused. */
export function ChangePasswordFlow() {
  const setOpen = useProfileModalStore((s) => s.setOpen);
  const isDealer = useIsDealer();
  const [step, setStep] = useState<Step>({ name: 'send' });
  const close = useCallback(() => setOpen(null), [setOpen]);
  const backToSend = useCallback(() => setStep({ name: 'send' }), []);

  if (step.name === 'send') {
    return (
      <SendCodeStep
        title={COPY.title}
        helper={COPY.helper}
        send={sendPasswordOtp}
        onSent={(dispatch) => setStep({ name: 'code', dispatch })}
        onCancel={close}
      />
    );
  }
  if (step.name === 'code') {
    return (
      <VerificationCodeStep
        dispatch={step.dispatch}
        // Grilling Q5c: a Dealer's password flow reads like the phone flow's.
        resendLabel={isDealer ? PROFILE_COPY.changePhone.resendLabel : COPY.resendLabel}
        verify={async (body) => {
          await verifyPasswordOtp(body);
          setStep({ name: 'new-password' });
        }}
        resend={resendPasswordOtp}
        onExpired={backToSend}
        onCancel={close}
      />
    );
  }
  return <NewPasswordStep onClose={close} onExpired={backToSend} />;
}
